/**
 * Utility functions for checking if a user is a new subscriber
 */
import { subscriptionService } from '../services';

export interface NewUserCheckResult {
  isNewUser: boolean;
  hasFlowerSubscription: boolean;
  hasFruitSubscription: boolean;
  totalSubscriptions: number;
  error?: string;
}

/**
 * Check if the user is a new subscriber by checking their subscription history
 * @returns Promise<NewUserCheckResult>
 */
export async function checkIsNewUser(): Promise<NewUserCheckResult> {
  try {
    // Check if user is authenticated
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        isNewUser: true,
        hasFlowerSubscription: false,
        hasFruitSubscription: false,
        totalSubscriptions: 0,
        error: 'User not authenticated'
      };
    }

    // Get all user subscriptions
    const subscriptions = await subscriptionService.getAllSubscriptions(false);
    
    // Count subscriptions by service type
    const flowerSubscriptions = subscriptions.filter(sub => 
      !sub.serviceType || sub.serviceType === 'flower'
    );
    const fruitSubscriptions = subscriptions.filter(sub => 
      sub.serviceType === 'fruit'
    );

    // Count only completed or active subscriptions (not pending_payment)
    const completedSubscriptions = subscriptions.filter(sub => 
      sub.status && ['active', 'paused', 'cancelled', 'expired'].includes(sub.status)
    );

    const isNewUser = completedSubscriptions.length === 0;
    const hasFlowerSubscription = flowerSubscriptions.some(sub => 
      sub.status && ['active', 'paused', 'cancelled', 'expired'].includes(sub.status)
    );
    const hasFruitSubscription = fruitSubscriptions.some(sub => 
      sub.status && ['active', 'paused', 'cancelled', 'expired'].includes(sub.status)
    );

    return {
      isNewUser,
      hasFlowerSubscription,
      hasFruitSubscription,
      totalSubscriptions: completedSubscriptions.length
    };
  } catch (error) {
    console.error('Error checking new user status:', error);
    return {
      isNewUser: false, // Default to false to avoid showing gift if there's an error
      hasFlowerSubscription: false,
      hasFruitSubscription: false,
      totalSubscriptions: 0,
      error: 'Failed to check subscription history'
    };
  }
}

/**
 * Check if user is eligible for first-time flower subscription gift
 * @returns Promise<boolean>
 */
export async function isEligibleForFlowerScissorsGift(): Promise<boolean> {
  try {
    const result = await checkIsNewUser();
    
    // User is eligible if they:
    // 1. Are a new user (no previous completed subscriptions)
    // 2. Have never had a flower subscription
    return result.isNewUser && !result.hasFlowerSubscription;
  } catch (error) {
    console.error('Error checking flower scissors gift eligibility:', error);
    return false;
  }
}

/**
 * Check if user is eligible for any new subscriber gifts based on service type
 * @param serviceType - The service type to check for
 * @returns Promise<boolean>
 */
export async function isEligibleForNewSubscriberGift(serviceType: 'flower' | 'fruit' | 'cat_grass'): Promise<boolean> {
  try {
    const result = await checkIsNewUser();
    
    if (serviceType === 'flower') {
      return result.isNewUser && !result.hasFlowerSubscription;
    } else if (serviceType === 'fruit') {
      return result.isNewUser && !result.hasFruitSubscription;
    } else if (serviceType === 'cat_grass') {
      // For cat_grass, we consider it similar to flower for now
      // You can modify this logic based on your business requirements
      return result.isNewUser && !result.hasFlowerSubscription;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking new subscriber gift eligibility:', error);
    return false;
  }
} 